import React, { useState, useEffect } from "react";
import api from "../api";

const Settings = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [msg, setMsg] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [storage, setStorage] = useState({ used_mb: 0, total_allotted_mb: 128 });
    const [intervals, setIntervals] = useState({
        ssl: 300,
        whois: 86400,
        port: 300,
        malware: 3600,
        dos: 60,
        server: 3600
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const results = await Promise.allSettled([
                    api.get("/api/auth/current-user"),
                    api.get("/api/settings/storage-usage"),
                    api.get("/api/settings/user-cron")
                ]);

                const [userRes, storageRes, cronRes] = results;

                if (userRes.status === 'fulfilled') {
                    setUsername(userRes.value.data.username);
                } else {
                    console.error("Failed to load user profile:", userRes.reason);
                }

                if (storageRes.status === 'fulfilled' && storageRes.value.data.success) {
                    setStorage(storageRes.value.data.data);
                } else {
                    console.error("Failed to load storage settings:", storageRes.reason);
                }

                if (cronRes.status === 'fulfilled' && cronRes.value.data.success) {
                    const c = cronRes.value.data.data;
                    setIntervals({
                        ssl: c.ssl_scan_interval,
                        whois: c.whois_scan_interval,
                        port: c.port_scan_interval,
                        malware: c.malware_scan_interval,
                        dos: c.dos_monitor_interval,
                        server: c.server_details_interval
                    });
                } else {
                    console.error("Failed to load cron settings:", cronRes.reason);
                }

                if (userRes.status !== 'fulfilled') {
                    setError("Failed to load user profile");
                }
                setLoading(false);
            } catch (err) {
                setError("Failed to load data");
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const [updateLoading, setUpdateLoading] = useState(false);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setError("");
        setMsg("");

        if (password && password.length < 6) {
            setError("New password must be at least 6 characters");
            return;
        }
        if (!username || username.trim().length < 2) {
            setError("Username must be at least 2 characters");
            return;
        }

        setUpdateLoading(true);
        try {
            await api.put("/api/auth/update-profile", { username: username.trim(), password: password || undefined });
            setMsg("Profile updated successfully");
            setPassword("");
        } catch (err) {
            setError(err.response?.data?.message || "Update failed. Please try again.");
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleIntervalUpdate = async (e) => {
        e.preventDefault();
        setError("");
        setMsg("");

        // Enforce minimum interval of 60 seconds to prevent hammering
        for (const [key, val] of Object.entries(intervals)) {
            if (!val || isNaN(val) || Number(val) < 60) {
                setError(`Interval for ${key.toUpperCase()} must be at least 60 seconds`);
                return;
            }
        }

        try {
            await api.post("/api/settings/user-cron", intervals);
            setMsg("Interval settings saved successfully");
        } catch (err) {
            setError("Failed to save intervals. Please try again.");
        }
    };

    if (loading) return <div className="text-center mt-5">Loading...</div>;

    const storagePercent = Math.min(100, (storage.used_mb / storage.total_allotted_mb) * 100);

    return (
        <div className="container mt-5 mb-5">
            <div className="row">
                <div className="col-md-6 mb-4">
                    {/* Account Settings */}
                    <div className="card shadow border-0 mb-4 h-100">
                        <div className="card-header bg-white border-0 pt-4">
                            <h3 className="card-title text-primary mb-0">
                                <i className="las la-cog me-2"></i> Account Settings
                            </h3>
                        </div>
                        <div className="card-body p-4">
                            <form onSubmit={handleUpdate}>
                                <div className="mb-3">
                                    <label className="form-label">Username</label>
                                    <input type="text" className="form-control" value={username} onChange={(e) => setUsername(e.target.value)} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">New Password</label>
                                    <div className="input-group">
                                        <input type={showPassword ? "text" : "password"} className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" />
                                        <button className="btn btn-outline-secondary" type="button" onClick={() => setShowPassword(!showPassword)}>
                                            <i className={showPassword ? "las la-eye-slash" : "las la-eye"}></i>
                                        </button>
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary w-100" disabled={updateLoading}>
                                    {updateLoading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Updating...</> : 'Update Profile'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 mb-4">
                    {/* Storage Usage & Cron */}
                    <div className="card shadow border-0 mb-4 bg-primary text-white">
                        <div className="card-body p-4">
                            <h5 className="card-title mb-3">
                                <i className="las la-database me-2"></i> Data Usage
                            </h5>
                            <div className="d-flex justify-content-between mb-2">
                                <span>{storage.used_mb} MB used</span>
                                <span>{storage.total_allotted_mb} MB allowed</span>
                            </div>
                            <div className="progress mb-2 bg-white bg-opacity-20" style={{ height: '10px' }}>
                                <div className="progress-bar bg-white" role="progressbar" style={{ width: `${storagePercent}%` }}></div>
                            </div>
                            <small className="opacity-75">Max allotted data used vs total allotted.</small>
                        </div>
                    </div>

                    <div className="card shadow border-0">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="card-title text-primary mb-0">
                                <i className="las la-clock me-2"></i> Scan Intervals
                            </h5>
                        </div>
                        <div className="card-body p-4">
                            <form onSubmit={handleIntervalUpdate}>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label small">SSL (s)</label>
                                        <input type="number" className="form-control form-control-sm" value={intervals.ssl} onChange={(e) => setIntervals({ ...intervals, ssl: parseInt(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">WHOIS (s)</label>
                                        <input type="number" className="form-control form-control-sm" value={intervals.whois} onChange={(e) => setIntervals({ ...intervals, whois: parseInt(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">Ports (s)</label>
                                        <input type="number" className="form-control form-control-sm" value={intervals.port} onChange={(e) => setIntervals({ ...intervals, port: parseInt(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">Malware (s)</label>
                                        <input type="number" className="form-control form-control-sm" value={intervals.malware} onChange={(e) => setIntervals({ ...intervals, malware: parseInt(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">DOS (s)</label>
                                        <input type="number" className="form-control form-control-sm" value={intervals.dos} onChange={(e) => setIntervals({ ...intervals, dos: parseInt(e.target.value) })} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">Server (s)</label>
                                        <input type="number" className="form-control form-control-sm" value={intervals.server} onChange={(e) => setIntervals({ ...intervals, server: parseInt(e.target.value) })} />
                                    </div>
                                </div>
                                <button type="submit" className="btn btn-primary btn-sm w-100 mt-4">Save Intervals</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {(msg || error) && (
                <div className={`alert ${msg ? 'alert-success' : 'alert-danger'} alert-dismissible fade show`} role="alert">
                    {msg || error}
                    <button type="button" className="btn-close" onClick={() => { setMsg(""); setError(""); }}></button>
                </div>
            )}
        </div>
    );
};

export default Settings;
